(() => {
  const choices = new Map();
  const attributeNames = [
    'Accord téléchargement immédiat',
    'Version accord numérique',
    'Date accord numérique (UTC)',
    'Panier accord numérique',
  ];
  let activeFingerprint = null;
  let pending = false;
  let permittedForm = null;
  let recording = Promise.resolve();

  const recordAttributes = attributes => {
    const root = window.Shopify?.routes?.root || '/';
    recording = recording.catch(() => {}).then(async () => {
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('Consent not saved');
      return response.json();
    });
    return recording;
  };

  const clearRecordedAgreement = consent => {
    if (consent.dataset.recorded !== 'true') return;
    consent.dataset.recorded = 'false';
    recordAttributes(Object.fromEntries(attributeNames.map(name => [name, ''])))
      .catch(() => { consent.dataset.recorded = 'true'; });
  };

  class BiboucheCartConsent extends HTMLElement {
    connectedCallback() {
      if (typeof MutationObserver === 'function') {
        this.observer = new MutationObserver(() => this.refresh(true));
        this.observer.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-fingerprint'],
        });
      }
      this.refresh(true);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    refresh(resetSelection = false) {
      const checkbox = this.querySelector('[data-consent-checkbox]');
      const error = this.querySelector('[data-consent-error]');
      if (!checkbox || !error) return;
      this.checkbox = checkbox;
      this.error = error;
      this.proof = [...this.querySelectorAll('[data-consent-proof]')];
      if (activeFingerprint !== this.dataset.fingerprint) {
        choices.clear();
        activeFingerprint = this.dataset.fingerprint;
      }
      if (resetSelection) this.checkbox.checked = choices.has(this.dataset.fingerprint);
      this.syncProof();
      if (!this.checkbox.checked) clearRecordedAgreement(this);
    }

    syncProof() {
      this.proof.forEach(input => { input.disabled = !this.checkbox.checked; });
      const date = choices.get(this.dataset.fingerprint);
      if (date) this.querySelector('[data-consent-date]').value = date;
    }

    clearError() {
      if (!this.error.textContent && this.error.hidden) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.checkbox.removeAttribute('aria-invalid');
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.checkbox.setAttribute('aria-invalid', 'true');
      this.checkbox.focus();
    }

    attributes() {
      return Object.fromEntries(
        [...this.querySelectorAll('input[name^="attributes["]')].map(input => [
          input.name.slice(11, -1),
          input.value,
        ])
      );
    }
  }

  customElements.define('bibouche-cart-consent', BiboucheCartConsent);

  const consentFor = form => [...document.querySelectorAll('bibouche-cart-consent')]
    .find(consent => consent.querySelector('[data-consent-checkbox]')?.form === form);

  const fingerprint = cart => cart.items.map(item => item.key + ':' + item.quantity + ';').join('');

  const getCart = async root => {
    const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };

  const checkout = async (form, button, consent) => {
    if (pending) return;
    consent.refresh();
    if (!consent.checkbox.checked) {
      consent.showError(consent.dataset.requiredMessage);
      return;
    }
    consent.clearError();
    pending = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    try {
      await recording.catch(() => {});
      const current = await getCart(root);
      const quantities = [...form.querySelectorAll('input[name="updates[]"]')];
      const quantitiesMatch = !quantities.length || (
        quantities.length === current.items.length &&
        quantities.every((input, index) => Number(input.value) === current.items[index].quantity)
      );
      if (fingerprint(current) !== consent.dataset.fingerprint || !quantitiesMatch) {
        choices.delete(consent.dataset.fingerprint);
        consent.checkbox.checked = false;
        consent.syncProof();
        consent.showError(consent.dataset.changedMessage);
        return;
      }
      if (!choices.has(consent.dataset.fingerprint)) {
        choices.set(consent.dataset.fingerprint, new Date().toISOString());
        consent.syncProof();
      }
      const attributes = consent.attributes();
      const saved = await recordAttributes(attributes);
      consent.dataset.recorded = 'true';
      if (
        fingerprint(saved) !== consent.dataset.fingerprint ||
        attributeNames.some(name => saved.attributes?.[name] !== attributes[name])
      ) {
        clearRecordedAgreement(consent);
        throw new Error('Consent not confirmed');
      }
      if (!consent.isConnected || !consent.checkbox.checked) {
        clearRecordedAgreement(consent);
        return;
      }
      permittedForm = form;
      button.disabled = false;
      form.requestSubmit(button);
    } catch {
      consent.showError(consent.dataset.saveMessage);
    } finally {
      pending = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"]');
    if (!button?.form) return;
    const consent = consentFor(button.form);
    if (!consent) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(button.form, button, consent);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    const consent = consentFor(form);
    if (!consent) return;
    if (permittedForm === form) {
      permittedForm = null;
      return;
    }
    const button = event.submitter?.name === 'checkout'
      ? event.submitter
      : document.querySelector('button[name="checkout"][form="' + form.id + '"]');
    if (!button || (event.submitter && event.submitter.name !== 'checkout')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(form, button, consent);
  }, true);

  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-consent-checkbox]')) {
      const consent = event.target.closest('bibouche-cart-consent');
      if (!consent) return;
      consent.refresh();
      if (consent.checkbox.checked) {
        choices.set(consent.dataset.fingerprint, new Date().toISOString());
      } else {
        choices.delete(consent.dataset.fingerprint);
        clearRecordedAgreement(consent);
      }
      consent.syncProof();
      consent.clearError();
      return;
    }
    if (!event.target.matches?.('input[name="updates[]"]')) return;
    const consent = consentFor(event.target.form);
    if (!consent) return;
    consent.refresh();
    choices.delete(consent.dataset.fingerprint);
    consent.checkbox.checked = false;
    consent.syncProof();
    clearRecordedAgreement(consent);
    consent.clearError();
  }, true);

  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    choices.clear();
    document.querySelectorAll('bibouche-cart-consent').forEach(consent => {
      consent.refresh();
      consent.checkbox.checked = false;
      consent.syncProof();
      clearRecordedAgreement(consent);
      consent.clearError();
    });
  });
})();
